import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Mail, Megaphone, BarChart2, FileCode, Plus } from "lucide-react";
import * as marketingService from "../services/marketingService";

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const Marketing = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("overview");
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    marketingService.fetchMarketingStats()
      .then((data) => { if (!cancelled) setStatsData(data); })
      .catch(() => { if (!cancelled) setStatsData({ campaigns: 0, emailsSent: 0, openRate: null, templates: 0 }); })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (tab !== "campaigns") return;
    setCampaignsLoading(true);
    marketingService.fetchCampaigns()
      .then((data) => setCampaigns(Array.isArray(data) ? data : []))
      .catch(() => setCampaigns([]))
      .finally(() => setCampaignsLoading(false));
  }, [tab]);

  const stats = [
    { labelKey: "marketing.campaigns", value: statsLoading ? "—" : String(statsData?.campaigns ?? 0), icon: Megaphone },
    { labelKey: "marketing.emailsSent", value: statsLoading ? "—" : String(statsData?.emailsSent ?? 0), icon: Mail },
    { labelKey: "marketing.openRate", value: statsLoading ? "—" : (statsData?.openRate != null ? `${statsData.openRate}%` : "—"), icon: BarChart2 },
    { labelKey: "marketing.templates", value: statsLoading ? "—" : String(statsData?.templates ?? 0), icon: FileCode },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title={t("marketing.title")}
        sub={t("marketing.subtitle")}
        actions={
          <Button $variant="outline" $size="sm">
            <Plus size={14} />
            {t("marketing.newCampaign")}
          </Button>
        }
      />

      <StatsGrid>
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.labelKey} style={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ borderRadius: "9999px", padding: 10, background: "rgba(155, 135, 245, 0.12)" }}>
                <Icon size={20} color="#9b87f5" />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{t(s.labelKey)}</p>
                <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: "#111827" }}>{s.value}</p>
              </div>
            </Card>
          );
        })}
      </StatsGrid>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("marketing.overview")}</TabsTrigger>
          <TabsTrigger value="campaigns">{t("marketing.campaigns")}</TabsTrigger>
          <TabsTrigger value="templates">{t("marketing.emailTemplates")}</TabsTrigger>
          <TabsTrigger value="analytics">{t("marketing.analytics")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card style={{ padding: 24, marginTop: 24 }}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
              {t("marketing.overviewDesc")}
            </p>
            <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 12 }}>
              <Button $variant="outline" $size="sm">
                <Megaphone size={14} />
                {t("marketing.createCampaign")}
              </Button>
              <Button $variant="outline" $size="sm">
                <FileCode size={14} />
                {t("marketing.newTemplate")}
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns">
          <div style={{ marginTop: 24 }}>
            {campaignsLoading ? (
              <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("marketing.loading")}</p>
            ) : campaigns.length === 0 ? (
              <Card style={{ padding: 24 }}>
                <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("marketing.noCampaignsYet")}</p>
              </Card>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {campaigns.map((c) => (
                  <Card key={c.id || c._id} style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ borderRadius: "9999px", padding: 8, background: "rgba(155, 135, 245, 0.12)" }}>
                        <Megaphone size={20} color="#9b87f5" />
                      </div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{c.name || c.subject || t("marketing.unnamedCampaign")}</h4>
                        {c.status && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>{c.status}</p>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <Card style={{ padding: 24, marginTop: 24 }}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("marketing.noTemplatesYet")}</p>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <Card style={{ padding: 24, marginTop: 24 }}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("marketing.analyticsPlaceholder")}</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Marketing;
